import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Download, 
  BookOpen, 
  Video, 
  FileText, 
  Calculator,
  CheckCircle,
  TrendingUp,
  Search,
  Target,
  Users
} from "lucide-react";

const Resources = () => {
  const freeResources = [
    {
      icon: Calculator,
      title: "ROI Calculator",
      description: "Calculate the potential return on investment from SEO and digital marketing campaigns.",
      type: "Tool",
      downloadLink: "#"
    },
    {
      icon: FileText,
      title: "SEO Audit Checklist",
      description: "50-point checklist to audit your website's SEO performance and identify improvement opportunities.",
      type: "PDF Guide",
      downloadLink: "#"
    },
    {
      icon: BookOpen,
      title: "Lead Generation Playbook",
      description: "Complete guide to generating high-quality leads through digital marketing channels.",
      type: "eBook",
      downloadLink: "#"
    },
    {
      icon: Target,
      title: "Local SEO Template",
      description: "Step-by-step template for optimizing your business for local search results.",
      type: "Template",
      downloadLink: "#"
    }
  ];

  const guides = [
    {
      title: "The Complete Guide to Local SEO for Service Businesses",
      description: "Learn how to dominate local search results and attract more customers in your area.",
      readTime: "12 min read",
      category: "SEO"
    },
    {
      title: "How to Create High-Converting Landing Pages",
      description: "Best practices for designing landing pages that turn visitors into leads and customers.",
      readTime: "8 min read", 
      category: "Conversion"
    },
    {
      title: "Google Ads vs. Facebook Ads: Which is Right for Your Business?",
      description: "Compare the pros and cons of each platform to make the best choice for your marketing budget.",
      readTime: "10 min read",
      category: "Paid Ads"
    },
    {
      title: "Marketing Automation for Small Businesses",
      description: "How to set up automated marketing systems that nurture leads and increase sales.",
      readTime: "15 min read",
      category: "Automation"
    }
  ];

  const videoResources = [
    {
      title: "SEO Fundamentals: Getting Started",
      description: "Learn the basics of SEO and how to improve your website's search rankings.",
      duration: "25 minutes"
    },
    {
      title: "Google Ads Setup Walkthrough",
      description: "Step-by-step guide to setting up your first Google Ads campaign.",
      duration: "18 minutes"
    },
    {
      title: "Content Marketing Strategy",
      description: "How to create and distribute content that attracts and converts customers.",
      duration: "22 minutes"
    }
  ];

  const tools = [
    {
      name: "Keyword Research Tool",
      description: "Find profitable keywords for your SEO and content marketing campaigns"
    },
    {
      name: "Website Speed Test",
      description: "Check your website's loading speed and get improvement recommendations"
    },
    {
      name: "Competitor Analysis Tool",
      description: "Analyze your competitors' SEO strategies and find opportunities"
    },
    {
      name: "Local SEO Checker",
      description: "Audit your local SEO performance and get actionable insights"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-primary via-primary to-primary-light">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Free <span className="text-secondary">Resources</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
              Discover proven strategies, tools, and templates to grow your business. 
              All resources are completely free and designed to deliver immediate value.
            </p>
          </div>
        </div>
      </section>

      {/* Free Downloads Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Free <span className="text-secondary">Downloads</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                Get instant access to our most valuable resources
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {freeResources.map((resource, index) => (
                <Card key={index} className="border-2 border-transparent hover:border-secondary/20 transition-all duration-300 hover:shadow-luxury hover:-translate-y-2">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center mx-auto mb-4">
                      <resource.icon className="w-8 h-8 text-secondary" />
                    </div>
                    <div className="inline-block bg-secondary/10 text-secondary px-3 py-1 rounded-full text-sm font-medium mb-2">
                      {resource.type}
                    </div>
                    <CardTitle className="text-xl text-foreground">{resource.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground mb-6">{resource.description}</p>
                    <Button variant="premium" size="sm" className="w-full">
                      <Download className="w-4 h-4 mr-2" />
                      Download Free
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Guides Section */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Expert <span className="text-secondary">Guides</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                In-depth articles and strategies from our digital marketing experts
              </p>
            </div>

            <div className="space-y-6">
              {guides.map((guide, index) => (
                <Card key={index} className="border-0 shadow-elegant hover:shadow-luxury transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-4">
                          <span className="bg-secondary/10 text-secondary px-3 py-1 rounded-full text-sm font-medium mr-3">
                            {guide.category}
                          </span>
                          <span className="text-sm text-muted-foreground">{guide.readTime}</span>
                        </div>
                        <h3 className="text-2xl font-bold text-foreground mb-3">{guide.title}</h3>
                        <p className="text-muted-foreground mb-4">{guide.description}</p>
                        <Button variant="ghost" className="text-secondary hover:text-secondary">
                          Read Guide →
                        </Button>
                      </div>
                      <BookOpen className="w-8 h-8 text-secondary ml-6 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Video Resources Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Video <span className="text-secondary">Tutorials</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                Learn through step-by-step video walkthroughs
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {videoResources.map((video, index) => (
                <Card key={index} className="border-2 border-transparent hover:border-secondary/20 transition-all duration-300 hover:shadow-luxury">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center mb-4">
                      <Video className="w-6 h-6 text-secondary" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-3">{video.title}</h3>
                    <p className="text-muted-foreground mb-4">{video.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{video.duration}</span>
                      <Button variant="ghost" size="sm" className="text-secondary hover:text-secondary">
                        Watch Now →
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Free Tools Section */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Free <span className="text-secondary">Tools</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                Powerful tools to analyze and improve your digital marketing
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {tools.map((tool, index) => (
                <Card key={index} className="border-0 shadow-elegant hover:shadow-luxury transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-2">{tool.name}</h3>
                        <p className="text-muted-foreground">{tool.description}</p>
                      </div>
                      <Button variant="premium" size="sm">
                        Use Tool
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-light">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Need Personalized Help?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              While our free resources are valuable, nothing beats a custom strategy 
              tailored specifically to your business. Book a free consultation to get started.
            </p>
            <Button variant="premium" size="xl" asChild>
              <a href="/book-call">
                Book Free Strategy Call
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;